import React from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { FilterButton } from './FilterButton';
import { cn } from '../../lib/utils';
import { useNoWheelHijack } from '../../hooks/useNoWheelHijack';

interface ListTabBarProps {
    /** The tab strip itself (a `Tabs`/`TabsList`, plus any sub-tab row). */
    children: React.ReactNode;
    /** Trailing controls — search box, `FilterButton`, etc. */
    actions?: React.ReactNode;
    className?: string;
}

/**
 * Header row for a list page: a tab strip on the left, search/filter controls
 * on the right.
 *
 * The layout differs by viewport because a single row cannot hold both on a
 * phone. Previously every list page put the tabs *and* the toolbar inside one
 * `overflow-x-auto` row, so on mobile the search box and the filter button were
 * pushed past the right edge and could only be reached by sideways-scrolling a
 * strip that gives no hint it scrolls — i.e. the filter control was effectively
 * missing (see AssetManagement/LicenseManagement/InventoryManagementModule).
 *
 * Here the two are separate rows below `md`: the tab strip scrolls horizontally
 * *within itself*, and the toolbar sits underneath at full width, always
 * visible. From `md` up it collapses back to the original single justified row.
 *
 * `min-w-0` on the tab strip is load-bearing — without it the flex item refuses
 * to shrink below its content width and pushes the whole page sideways.
 */
export const ListTabBar: React.FC<ListTabBarProps> = ({ children, actions, className }) => {
    const scrollRef = useNoWheelHijack<HTMLDivElement>();

    return (
        <div className={cn('border-b bg-card/50', className)}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between md:gap-4">
                <div
                    ref={scrollRef}
                    className="tab-strip-rows flex min-w-0 max-w-full flex-col px-4 sm:px-6 md:flex-row md:items-center md:gap-4 md:pr-0"
                >
                    {children}
                </div>
                {actions && (
                    <div className="flex min-w-0 items-center gap-2 px-4 pb-3 sm:px-6 md:shrink-0 md:pb-0 md:pl-0">
                        {actions}
                    </div>
                )}
            </div>
        </div>
    );
};

interface ListToolbarProps {
    searchValue: string;
    onSearchChange: (value: string) => void;
    searchPlaceholder?: string;
    activeFilterCount: number;
    onOpenFilters: () => void;
    /** Clears search *and* filters. The Clear control only renders when set. */
    onClearAll?: () => void;
    /** Whether anything is currently applied — gates the Clear control. */
    hasActive?: boolean;
    className?: string;
}

/**
 * The search + filter row that sits directly beneath a `ListTabBar`, matching
 * the Inventory module's structure: tabs on their own row, then a full-width
 * toolbar, then the applied-filter chips.
 *
 * Layout by viewport:
 * - **Mobile**: one row split 80/20 — search takes `basis-4/5`, the filter
 *   button `basis-1/5`. `FilterButton` already drops its text label below `sm`,
 *   so the icon sits comfortably in that fifth. The search field keeps
 *   `min-w-0` so it can give ground to the Clear control when one is showing
 *   rather than pushing the row wider than the screen.
 * - **`md` and up**: `md:contents` dissolves the mobile wrapper, so the search
 *   field and the action group become direct children of the justified row —
 *   search hard left, filter/clear hard right.
 */
export const ListToolbar: React.FC<ListToolbarProps> = ({
    searchValue,
    onSearchChange,
    searchPlaceholder = 'Search…',
    activeFilterCount,
    onOpenFilters,
    onClearAll,
    hasActive = false,
    className,
}) => (
    <div
        className={cn(
            'flex flex-col gap-2 border-b p-4 md:flex-row md:items-center md:justify-between md:gap-4',
            className
        )}
    >
        <div className="flex items-center gap-2 md:contents">
            <div className="relative min-w-0 basis-4/5 md:w-96 md:flex-none md:basis-auto">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    type="search"
                    className="pl-9"
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                />
            </div>
            <div className="flex basis-1/5 items-center justify-end gap-2 md:basis-auto">
                <FilterButton
                    activeCount={activeFilterCount}
                    onClick={onOpenFilters}
                    className="w-full md:w-auto"
                />
                {onClearAll && hasActive && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={onClearAll}
                        aria-label="Clear search and filters"
                        className="shrink-0 cursor-pointer px-2 text-muted-foreground"
                    >
                        <X className="h-4 w-4 md:mr-1" />
                        <span className="hidden md:inline">Clear</span>
                    </Button>
                )}
            </div>
        </div>
    </div>
);
